import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Camera, Compass, MessagesSquare, Sparkles } from "lucide-react";
import ErrorPageLayout from "@/components/common/ErrorPageLayout";
import { isBrowser } from "@/utils/runtime";

interface QuickLink {
  title: string;
  description: string;
  to?: string;
  href?: string;
  icon: React.ReactNode;
  accent: "orange" | "blue" | "brown" | "beige";
}

const quickLinks: QuickLink[] = [
  {
    title: "Conheça nossos serviços",
    description: "Descubra como a Toc Toc transforma imóveis em experiências visuais que vendem mais.",
    to: "/servicos",
    icon: <Sparkles className="h-5 w-5" aria-hidden="true" />,
    accent: "orange",
  },
  {
    title: "Explore o portfólio",
    description: "Veja produções recentes de fotografia, vídeo e tours imersivos que entregamos para nossos clientes.",
    to: "/portfolio",
    icon: <Camera className="h-5 w-5" aria-hidden="true" />,
    accent: "blue",
  },
  {
    title: "Central de ajuda",
    description: "Encontre respostas rápidas sobre prazos, entregas, formatos e suporte técnico.",
    to: "/centraldeajuda",
    icon: <BookOpen className="h-5 w-5" aria-hidden="true" />,
    accent: "brown",
  },
  {
    title: "Fale com um especialista",
    description: "Converse pelo WhatsApp com o time Toc Toc para receber orientações imediatas.",
    href: "https://wa.me/5547997367396",
    icon: <MessagesSquare className="h-5 w-5" aria-hidden="true" />,
    accent: "orange",
  },
];

const accentStyles: Record<QuickLink["accent"], { icon: string; highlight: string }> = {
  orange: {
    icon: "bg-toctoc-orange/15 text-toctoc-orange",
    highlight: "text-toctoc-orange",
  },
  blue: {
    icon: "bg-toctoc-blue/15 text-toctoc-blue",
    highlight: "text-toctoc-blue",
  },
  brown: {
    icon: "bg-toctoc-brown/10 text-toctoc-brown",
    highlight: "text-toctoc-brown",
  },
  beige: {
    icon: "bg-toctoc-beige/60 text-toctoc-black",
    highlight: "text-toctoc-black",
  },
};

const NotFound: React.FC = () => {
  useEffect(() => {
    if (!isBrowser) return;

    globalThis.document.title = "Página não encontrada | Toc Toc";

    const metaDescription = globalThis.document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "A página que você tentou acessar não existe ou foi movida. Continue navegando pelos serviços da Toc Toc."
      );
    }

    let metaRobots = globalThis.document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = globalThis.document.createElement("meta");
      metaRobots.setAttribute("name", "robots");
      globalThis.document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute("content", "noindex, follow");
  }, []);

  return (
    <ErrorPageLayout
      statusCode="404"
      title="Essa porta está fechada no momento"
      description="Não encontramos o endereço que você buscou, mas temos outras experiências esperando por você."
      details="Use os atalhos abaixo para continuar explorando a plataforma Toc Toc."
      primaryAction={{
        label: "Voltar para o início",
        to: "/",
        icon: <Compass className="h-4 w-4" aria-hidden="true" />,
      }}
      secondaryAction={{
        label: "Falar com a Toc Toc",
        href: "https://wa.me/5547997367396",
        icon: <MessagesSquare className="h-4 w-4" aria-hidden="true" />,
      }}
    >
      <div className="mx-auto grid w-full max-w-4xl gap-6 md:grid-cols-2">
        {quickLinks.map((link) => {
          const content = (
            <div className="group flex h-full flex-col justify-between rounded-3xl border border-white/40 bg-white/90 p-6 text-left shadow-xl shadow-toctoc-orange/5 transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-toctoc-orange/10">
              <div>
                <span
                  className={`inline-flex items-center justify-center rounded-full p-3 ${accentStyles[link.accent].icon}`}
                >
                  {link.icon}
                </span>
                <h3 className="mt-4 text-xl font-semibold text-toctoc-black">
                  {link.title}
                </h3>
                <p className="mt-2 text-sm text-toctoc-gray-medium">
                  {link.description}
                </p>
              </div>
              <span className={`mt-6 text-sm font-semibold ${accentStyles[link.accent].highlight}`}>
                {link.to ? "Acessar página" : "Abrir conversa"} →
              </span>
            </div>
          );

          if (link.to) {
            return (
              <Link key={link.title} to={link.to} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-toctoc-orange/40">
                {content}
              </Link>
            );
          }

          return (
            <a
              key={link.title}
              href={link.href}
              target={link.href?.startsWith("http") ? "_blank" : undefined}
              rel={link.href?.startsWith("http") ? "noopener noreferrer" : undefined}
              className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-toctoc-orange/40"
            >
              {content}
            </a>
          );
        })}
      </div>
    </ErrorPageLayout>
  );
};

export default NotFound;
